import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { ArrowLeft, Clock, Calendar, User, Share2, BookOpen } from "lucide-react";
import ReviewedBy from "@/components/ReviewedBy";
import Breadcrumbs from "@/components/Breadcrumbs";
import AdSlot from "@/components/AdSlot";
import WhatsAppPill from "@/components/WhatsAppPill";
import TelegramPill from "@/components/TelegramPill";
import useNoindexOnNotFound from "@/hooks/useNoindexOnNotFound";

const AD_SLOT_BLOG_TOP = process.env.REACT_APP_AD_SLOT_BLOG_TOP || "";
const AD_SLOT_BLOG_BOT = process.env.REACT_APP_AD_SLOT_BLOG_BOT || "";

const SITE_URL = "https://hireds.in";

function renderMarkdown(md) {
  if (!md) return "";
  // Simple markdown-to-HTML for our limited subset (## headings, **bold**, paragraphs)
  const lines = md.split("\n");
  const out = [];
  let buf = [];
  const flush = () => {
    if (buf.length) {
      const text = buf.join(" ").replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
      out.push(`<p>${text}</p>`);
      buf = [];
    }
  };
  for (const ln of lines) {
    const t = ln.trim();
    if (!t) { flush(); continue; }
    if (t.startsWith("### ")) { flush(); out.push(`<h3>${t.slice(4)}</h3>`); }
    else if (t.startsWith("## ")) { flush(); out.push(`<h2>${t.slice(3)}</h2>`); }
    else if (t.startsWith("# ")) { flush(); out.push(`<h2>${t.slice(2)}</h2>`); }
    else if (t.startsWith("- ")) {
      flush();
      if (!out.length || !out[out.length-1].startsWith("<ul")) out.push("<ul>");
      out[out.length-1] = out[out.length-1] + `<li>${t.slice(2)}</li>`;
    } else { buf.push(t); }
  }
  flush();
  return out.map(s => s.startsWith("<ul>") ? s+"</ul>" : s).join("\n");
}

function buildArticleJsonLd(article) {
  if (!article) return null;
  const url = `${SITE_URL}/blog/${article.slug}`;
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": article.title,
    "description": article.excerpt,
    "datePublished": article.published_date,
    "dateModified": article.updated_date || article.published_date,
    "author": { "@type": "Person", "name": article.author || "Dileshwar Vitthale" },
    "publisher": {
      "@type": "Organization",
      "name": "hireds.in",
      "logo": { "@type": "ImageObject", "url": `${SITE_URL}/icon-512.png` },
    },
    "mainEntityOfPage": { "@type": "WebPage", "@id": url },
    "url": url,
    "wordCount": article.word_count,
    "image": `${SITE_URL}/og-image.png`,
    "articleSection": article.category,
    "keywords": (article.tags || []).join(", "),
  };
}

function useArticleSeo(article) {
  useEffect(() => {
    if (!article) return;
    const oldTitle = document.title;
    document.title = `${article.title} | hireds.in Blog`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const oldDesc = metaDesc?.content;
    if (metaDesc) metaDesc.content = (article.excerpt || article.title).slice(0, 160);
    const canonical = document.querySelector('link[rel="canonical"]');
    const oldCanonical = canonical?.href;
    if (canonical) canonical.href = `${SITE_URL}/blog/${article.slug}`;
    let el = document.getElementById("article-jsonld");
    if (!el) {
      el = document.createElement("script");
      el.type = "application/ld+json";
      el.id = "article-jsonld";
      document.head.appendChild(el);
    }
    el.textContent = JSON.stringify(buildArticleJsonLd(article));
    return () => {
      document.title = oldTitle;
      if (metaDesc && oldDesc !== undefined) metaDesc.content = oldDesc;
      if (canonical && oldCanonical) canonical.href = oldCanonical;
      const cur = document.getElementById("article-jsonld");
      if (cur) cur.remove();
    };
  }, [article]);
}

export default function BlogDetail() {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    window.scrollTo(0, 0);
    api.get(`/articles/${slug}`).then(r=>setArticle(r.data)).catch(()=>setArticle(false)).finally(()=>setLoading(false));
  }, [slug]);

  useArticleSeo(article && typeof article === "object" ? article : null);
  useNoindexOnNotFound(article === false);

  if (loading) return <div className="max-w-3xl mx-auto px-4 py-12 text-slate-500">Loading…</div>;
  if (!article) return <div className="max-w-3xl mx-auto px-4 py-12 text-slate-500" data-testid="article-not-found">This article is no longer available. Browse the <Link to="/blog" className="text-orange-600 underline">latest guides</Link>.</div>;

  const url = `${SITE_URL}/blog/${article.slug}`;
  const html = renderMarkdown(article.content);

  return (
    <article className="max-w-3xl mx-auto px-4 md:px-6 py-8" data-testid="blog-detail">
      <Breadcrumbs items={[
        { label: "Home", to: "/" },
        { label: "Blog", to: "/blog" },
        { label: article.title },
      ]} />
      <Link to="/blog" className="text-sm text-slate-600 hover:text-orange-600 inline-flex items-center gap-1 mb-4"><ArrowLeft className="w-4 h-4"/>Back to Blog</Link>

      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-xs font-bold uppercase tracking-wider">
        <BookOpen className="w-3 h-3"/>{article.category.replace("-", " ")}
      </span>
      <h1 className="text-3xl md:text-5xl font-extrabold tracking-tight mt-3 leading-tight" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>{article.title}</h1>
      <p className="text-lg text-slate-600 mt-3 leading-relaxed">{article.excerpt}</p>
      <ReviewedBy date={article.updated_date || article.published_date} reviewer={article.author || "Dileshwar Vitthale"} />

      <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-slate-500 border-y border-slate-200 py-3">
        <span className="inline-flex items-center gap-1.5"><User className="w-4 h-4"/>{article.author}</span>
        <span className="inline-flex items-center gap-1.5"><Calendar className="w-4 h-4"/><time dateTime={(article.published_date||"").slice(0,10)}>{fmtDate(article.published_date)}</time></span>
        <span className="inline-flex items-center gap-1.5"><Clock className="w-4 h-4"/>{article.reading_time} min read</span>
        <span className="inline-flex items-center gap-1.5">{article.word_count} words</span>
      </div>

      {/* AdSense slot — top */}
      <AdSlot slot={AD_SLOT_BLOG_TOP} className="my-8" />

      <div className="prose prose-slate max-w-none mt-2 prose-headings:font-extrabold prose-headings:tracking-tight prose-h2:mt-10 prose-h2:mb-3 prose-h2:text-2xl prose-h3:mt-7 prose-h3:mb-2 prose-h3:text-lg prose-p:leading-relaxed prose-p:text-slate-700 prose-li:text-slate-700 prose-strong:text-slate-900 prose-a:text-orange-600 prose-a:no-underline hover:prose-a:underline" dangerouslySetInnerHTML={{__html: html}}/>

      {/* AdSense slot — bottom */}
      <AdSlot slot={AD_SLOT_BLOG_BOT} className="my-10" />

      <WhatsAppPill variant="banner" className="mt-6" testId="blog-whatsapp-banner" />
      <TelegramPill variant="banner" className="mt-3" testId="blog-telegram-banner" />

      <div className="mt-10 pt-6 border-t border-slate-200">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500 inline-flex items-center gap-1.5"><Share2 className="w-3.5 h-3.5"/>Share this article</span>
          <a data-testid="article-share-whatsapp" href={`https://api.whatsapp.com/send?text=${encodeURIComponent(article.title + " " + url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on WhatsApp" className="px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider text-white bg-green-600 hover:bg-green-700">WhatsApp</a>
          <a data-testid="article-share-twitter" href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(article.title)}&url=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Twitter" className="px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider text-white bg-slate-900 hover:bg-slate-800">Twitter</a>
          <a data-testid="article-share-facebook" href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`} target="_blank" rel="noopener noreferrer" aria-label="Share on Facebook" className="px-3 py-1.5 rounded-md text-xs font-semibold uppercase tracking-wider text-white bg-blue-600 hover:bg-blue-700">Facebook</a>
        </div>
      </div>

      {Array.isArray(article.related) && article.related.length > 0 && (
        <section className="mt-12">
          <h2 className="text-xl font-extrabold mb-4" style={{fontFamily:"'Cabinet Grotesk',sans-serif"}}>Related Articles</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            {article.related.map((r, i) => (
              <Link key={r.id} to={`/blog/${r.slug}`} data-testid={`related-${i}`} className="bg-white border border-slate-200 rounded-lg p-4 hover:border-orange-300 transition-colors">
                <div className="text-xs uppercase tracking-wider font-bold text-orange-600">{r.category.replace("-"," ")}</div>
                <div className="font-semibold text-slate-900 mt-1 leading-snug">{r.title}</div>
                <div className="text-xs text-slate-500 mt-2 inline-flex items-center gap-1"><Clock className="w-3 h-3"/>{r.reading_time} min read</div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}
